/**
 * AllUsersDirectory — platform-admin global directory of every user across
 * every firm (admin dashboard spec §7). Reads GET /api/v1/admin/all-users
 * (admin-gated). Firm-first: users are grouped under their resolved firm, with
 * search (name/email) and a firm filter. Each row carries a "View as" action
 * that activates the seat lens as that person — wired to Clerk impersonation in
 * the next increment (§8); until then the control is present but inert.
 *
 * NOTE (verified on deployed DB 2026-10-06): firm resolution uses the canonical
 * link law_firms.clerk_org_id == users.org_id. On current prod data NO firm is
 * Clerk-connected yet, so everyone lands under "— No firm —" until a firm is
 * connected (connect_clerk_org). That is a data-state, not a UI bug.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, UserRound, ArrowRightLeft, Building2 } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';

export interface DirectoryUser {
  user_id: string;
  clerk_user_id: string;
  email: string;
  name: string;
  role: string;
  role_display: string;
  org_id?: string | null;
  firm_id?: string | null;
  firm_name?: string | null;
  client_side?: string | null;
  created_at?: string | null;
  last_login?: string | null;
}

interface FirmRollup {
  firm_id: string | null;
  firm_name: string | null;
  count: number;
}

interface AllUsersResponse {
  total: number;
  users: DirectoryUser[];
  firms: FirmRollup[];
}

const NO_FIRM = 'No firm';

const STAFF_ROLES = new Set(['owner', 'admin', 'analyst', 'viewer']);

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// Compact the verbose external/internal suffix so the badge fits one line in its column
// (K3: "ACCOUNTANT (EXTERNAL)" → "ACCOUNTANT · EXT"), without losing the distinction.
const compactRole = (display: string) =>
  display.replace(/\s*\(EXTERNAL\)/i, ' · EXT').replace(/\s*\(INTERNAL\)/i, ' · INT');

function RoleBadge({ role, display }: { role: string; display: string }) {
  const r = (role || '').toLowerCase();
  const label = compactRole(display);
  // Rank by contrast, not hue (K3): OWNER = inverted solid chip (max-neutral = top of the
  // hierarchy); every other role = hairline outline. No brand orange on a role badge.
  if (r === 'owner') {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-mono uppercase tracking-wide whitespace-nowrap"
        style={{ background: 'var(--color-text-primary)', color: 'var(--color-bg-base)' }}>
        {label}
      </span>
    );
  }
  const color = STAFF_ROLES.has(r) ? 'var(--color-text-secondary)' : 'var(--color-text-muted)';
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-mono uppercase tracking-wide whitespace-nowrap"
      style={{ color, borderColor: color }}
    >
      {label}
    </span>
  );
}

interface AllUsersDirectoryProps {
  /** Wired in the view-as increment (§8). When absent the control is inert. */
  onViewAs?: (user: DirectoryUser) => void;
}

export default function AllUsersDirectory({ onViewAs }: AllUsersDirectoryProps) {
  const { getToken } = useAuth();
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [firms, setFirms] = useState<FirmRollup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [firmFilter, setFirmFilter] = useState<string>('all');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const data = await api.get<AllUsersResponse>('/api/v1/admin/all-users', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) {
          setUsers(data?.users || []);
          setFirms(data?.firms || []);
        }
      } catch (e: any) {
        if (active) setError(e?.message || 'Could not load the user directory.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [getToken]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => {
      if (firmFilter === 'none' && u.firm_id) return false;
      if (firmFilter !== 'all' && firmFilter !== 'none' && u.firm_id !== firmFilter) return false;
      if (!needle) return true;
      return `${u.name} ${u.email}`.toLowerCase().includes(needle);
    });
  }, [users, q, firmFilter]);

  // Group by firm, firmed sections first (alpha), "No firm" last.
  const groups = useMemo(() => {
    const byFirm = new Map<string, { label: string; firmId: string | null; clientSide?: string | null; rows: DirectoryUser[] }>();
    for (const u of filtered) {
      const key = u.firm_id || 'none';
      const label = u.firm_name || NO_FIRM;
      if (!byFirm.has(key)) byFirm.set(key, { label, firmId: u.firm_id ?? null, clientSide: u.client_side, rows: [] });
      byFirm.get(key)!.rows.push(u);
    }
    return Array.from(byFirm.values()).sort((a, b) => {
      const aNo = a.firmId === null;
      const bNo = b.firmId === null;
      if (aNo !== bNo) return aNo ? 1 : -1;
      return (a.label || '').localeCompare(b.label || '');
    });
  }, [filtered]);

  const firmedCount = useMemo(() => users.filter((u) => u.firm_id).length, [users]);

  return (
    <div className="space-y-6">
      {/* Plain section header — no orange fill (K3 gate: orange reserved for nav + primary action). */}
      <div className="flex items-center gap-3">
        <UserRound className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        <div>
          <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>All Users</h2>
          <p className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>
            Every user across every firm, grouped by firm
          </p>
        </div>
      </div>

      <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
        {/* Summary + controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-6 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            <span><strong style={{ color: 'var(--color-text-primary)' }}>{users.length}</strong> users</span>
            <span><strong style={{ color: 'var(--color-text-primary)' }}>{firmedCount}</strong> in a firm</span>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={firmFilter}
              onChange={(e) => setFirmFilter(e.target.value)}
              className="py-2 px-3 rounded-lg text-sm"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              <option value="all">All firms</option>
              {firms.filter((f) => f.firm_id).map((f) => (
                <option key={f.firm_id!} value={f.firm_id!}>{f.firm_name} ({f.count})</option>
              ))}
              <option value="none">{NO_FIRM}</option>
            </select>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-dim)' }} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name or email…"
                className="pl-9 pr-3 py-2 rounded-lg text-sm w-64 max-w-full"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm" style={{ color: 'var(--color-status-adverse)' }}>{error}</p>
        ) : filtered.length === 0 ? (
          <p className="py-12 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {users.length === 0 ? 'No users found.' : 'No users match your filters.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            {/* ONE table so columns align across every firm group (K3): fixed layout +
                colgroup widths; firm groups are full-span section rows. */}
            <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '22%' }} />
                <col />
                <col style={{ width: '180px' }} />
                <col style={{ width: '110px' }} />
                <col style={{ width: '116px' }} />
              </colgroup>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  {['Name', 'Email', 'Role'].map((h) => (
                    <th key={h} className="text-left font-mono text-[11px] font-normal uppercase tracking-wide py-2 pr-4" style={{ color: 'var(--color-text-dim)' }}>{h}</th>
                  ))}
                  <th className="text-right font-mono text-[11px] font-normal uppercase tracking-wide py-2 pr-4" style={{ color: 'var(--color-text-dim)' }}>Joined</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <React.Fragment key={g.firmId ?? 'none'}>
                    <tr>
                      <td colSpan={5} className="pt-5 pb-2">
                        <div className="flex items-center gap-2">
                          {g.firmId && <Building2 className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />}
                          <span className="font-mono text-xs uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>{g.label}</span>
                          {g.clientSide === 'carrier' && (
                            <span className="font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                              style={{ color: 'var(--color-text-muted)', border: '1px solid var(--color-text-muted)' }}>
                              carrier
                            </span>
                          )}
                          <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>· {g.rows.length}</span>
                        </div>
                      </td>
                    </tr>
                    {g.rows.map((u) => (
                      <tr key={u.user_id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                        <td className="py-3 pr-4 truncate" title={u.name} style={{ color: 'var(--color-text-primary)' }}>{u.name}</td>
                        <td className="py-3 pr-4 truncate" title={u.email} style={{ color: 'var(--color-text-secondary)' }}>{u.email || '—'}</td>
                        <td className="py-3 pr-4 overflow-hidden"><RoleBadge role={u.role} display={u.role_display} /></td>
                        <td className="py-3 pr-4 text-right font-mono text-xs tabular-nums whitespace-nowrap" style={{ color: 'var(--color-text-muted)' }}>{fmtDate(u.created_at)}</td>
                        <td className="py-3 text-right">
                          <button
                            type="button"
                            onClick={() => onViewAs?.(u)}
                            disabled={!onViewAs}
                            title={onViewAs ? `View as ${u.name}` : 'View-as wiring lands in the next increment'}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono uppercase tracking-wide whitespace-nowrap border transition-colors ${onViewAs ? 'cursor-pointer hover:text-[var(--color-needs-human-text)] hover:border-[var(--color-needs-human-text)]' : 'cursor-not-allowed'}`}
                            style={{
                              color: onViewAs ? 'var(--color-text-secondary)' : 'var(--color-text-dim)',
                              borderColor: 'var(--color-border)',
                              opacity: onViewAs ? 1 : 0.5,
                            }}
                          >
                            <ArrowRightLeft className="w-3 h-3" />
                            View as
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
